import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { workflowExecutionEngine, type WorkflowExecution } from '@/services/workflowExecutionEngine';
import { CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';

interface WorkflowExecutionMonitorProps {
  executionId: string | null;
  onClose?: () => void;
}

export function WorkflowExecutionMonitor({ executionId, onClose }: WorkflowExecutionMonitorProps) {
  const [execution, setExecution] = useState<WorkflowExecution | null>(null);

  useEffect(() => {
    if (!executionId) {
      setExecution(null);
      return;
    }

    // Poll for execution status
    const interval = setInterval(() => {
      const status = workflowExecutionEngine.getExecutionStatus(executionId);
      if (status) {
        setExecution(status);
        
        // Stop polling if execution is complete or failed
        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(interval);
          
          // Auto-close after 3 seconds if completed
          if (status.status === 'completed') {
            setTimeout(() => {
              onClose?.();
            }, 3000);
          }
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [executionId, onClose]);

  if (!execution) return null;

  const progress = ((execution.currentNodeIndex + 1) / execution.nodeResults.length) * 100;

  return (
    <Card className="border-primary/20 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">Workflow Execution</CardTitle>
          <Badge variant={
            execution.status === 'completed' ? 'default' : 
            execution.status === 'failed' ? 'destructive' : 
            'secondary'
          }>
            {execution.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Step {execution.currentNodeIndex + 1} of {execution.nodeResults.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Node Results */}
        <div className="space-y-2">
          {execution.nodeResults.map((result, index) => (
            <div 
              key={result.nodeId}
              className={`flex items-center gap-2 text-xs p-2 rounded ${
                result.status === 'success' ? 'bg-green-50 dark:bg-green-900/20' :
                result.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                result.status === 'running' ? 'bg-blue-50 dark:bg-blue-900/20' :
                'bg-gray-50 dark:bg-gray-900/20'
              }`}
            >
              {result.status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
              {result.status === 'error' && <XCircle className="w-4 h-4 text-red-500" />}
              {result.status === 'running' && <Clock className="w-4 h-4 text-blue-500 animate-spin" />}
              {result.status === 'pending' && <AlertCircle className="w-4 h-4 text-gray-400" />}
              
              <div className="flex-1">
                <div className="font-medium">Step {index + 1}</div>
                {result.error && (
                  <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                    {result.error}
                  </div>
                )}
                {result.output && result.output.success && (
                  <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                    ✓ {result.output.message || 'Completed successfully'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Duration */}
        {execution.endTime && (
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            Completed in {Math.round((execution.endTime.getTime() - execution.startTime.getTime()) / 1000)}s
          </div>
        )}
      </CardContent>
    </Card>
  );
}
